<script lang="ts" setup>
import type { LineChartData, LineChartOptions } from '~/components/nrjx/chart/line/index.vue';
import type { BaselineVariableType } from '../../types';
import type { ScatterChartData, ScatterChartOptions } from '~/components/nrjx/chart/scatter/index.vue';

// ================================
// Composables
// ================================
const { t } = useI18n();
const { formatConsumption, formatNumberToIsoNumber } = useNumbers();
const { variablesItems } = useBaselineConstants();
const { getTailwindColor } = useTailwind();
const { baselinePrediction, baselinePredictionLoading, baseline } = storeToRefs(baselineStore());

// ================================
// Data
// ================================
const selectedChartType = ref<BaselineVariableType>('degree_days');

// ================================
// Computed
// ================================
const variablesItemsFiltered = computed(() => {
  return variablesItems.value.filter((item) => baseline.value?.model.variables.some((v) => v.name === item.value));
});

const relationshipData = computed(() => {
  const points: ScatterChartData['points'] = [];
  baselinePrediction.value?.points.forEach((item, originalIndex) => {
    const x = item.X[selectedChartType.value];
    const y = item.Y;
    if (x && y) points.push({ x, y, color: getTailwindColor('green-500'), originalIndex });
  });
  return { points };
});

const predictedData = computed(() => {
  const chartData: LineChartData = {
    keys: baselinePrediction.value?.points.map((item) => item.start_date) ?? [],
    lines: [
      {
        color: getTailwindColor('green-500'),
        values: baselinePrediction.value?.points.map((item) => item.Y ?? 0) ?? [],
      },
      {
        color: getTailwindColor('purple-500'),
        values: baselinePrediction.value?.points.map((item) => item.prediction ?? 0) ?? [],
      },
    ],
  };
  return chartData;
});

// ================================
// Options
// ================================
const predictedChartOptions = computed(
  (): LineChartOptions => ({
    height: 'h-[400px]',
    unit: 'kWh',
    tooltip: (dataIndex: number) => {
      const item = baselinePrediction.value?.points[dataIndex];
      if (!item) return undefined;
      return {
        body: [
          {
            color: getTailwindColor('purple-500'),
            title: t('baseline.predicted'),
            value: formatConsumption(item.prediction, 0),
          },
          {
            color: getTailwindColor('green-500'),
            title: t('baseline.actual'),
            value: formatConsumption(item.Y, 0),
          },
        ],
        footer: item.end_date,
      };
    },
  }),
);

const relationshipChartOptions = computed(
  (): ScatterChartOptions => ({
    height: 'h-[400px]',
    unit: 'kWh',
    trendLineColor: getTailwindColor('purple-500'),
    tooltip: (dataIndex: number) => {
      const item = baselinePrediction.value?.points[dataIndex];
      if (!item) return undefined;
      const x = item.X[selectedChartType.value];
      const y = item.Y;
      const unit = selectedChartType.value === 'degree_days' ? '°C' : 'kWh';
      return {
        body: [
          {
            title: t(`global.consumption`),
            value: formatConsumption(y, 0),
          },
          {
            title: t(`baseline.${selectedChartType.value}`),
            value: formatNumberToIsoNumber(x ?? 0, 1) + ' ' + unit,
          },
        ],
      };
    },
  }),
);
</script>
<template>
  <div class="bg-gray-50 rounded-lg p-4 mt-4">
    <h2 class="text-sm font-bold text-gray-500">
      {{ $t('baseline.visualization_title') }}
    </h2>
    <p class="text-xs text-gray-500 mb-4">
      {{ $t('baseline.visualization_description') }}
    </p>
    <nrjx-chart-line :loading="baselinePredictionLoading" :data="predictedData" :options="predictedChartOptions" />
  </div>
  <div class="bg-gray-50 rounded-lg p-4 mt-4">
    <div class="flex items-center justify-between mb-4">
      <div class="">
        <h2 class="text-sm font-bold text-gray-500">
          {{ $t('baseline.visualization_title_2') }}
        </h2>
        <p class="text-xs text-gray-500">
          {{ $t('baseline.visualization_description_2') }}
        </p>
      </div>

      <u-select v-model:model-value="selectedChartType" size="sm" :items="variablesItemsFiltered" />
    </div>
    <nrjx-chart-scatter :loading="baselinePredictionLoading" :data="relationshipData" :options="relationshipChartOptions" />
  </div>
</template>
